import { useMedia } from "./useMedia";
import imageCompression from "browser-image-compression";
import { toast } from "react-hot-toast";
import { t } from "i18next";

interface UseFileUploaderOptions {
  workorderId: string;
  uploadType: "photos" | "signatures";
  contactId?: string;
  firstname?: string;
  lastname?: string;
  function?: string;
}

// Configuration de compression
const COMPRESSION_OPTIONS = {
  maxSizeMB: 2, // Taille maximale en MB
  maxWidthOrHeight: 1920, // Résolution maximale
  useWebWorker: true, // Utilise un Web Worker pour ne pas bloquer l'UI
  fileType: "image/jpeg", // Format de sortie
  quality: 0.8, // Qualité de compression (0.8 = 80%)
};

// Vérification de la taille du fichier
const MAX_FILE_SIZE_MB = 10; // Taille maximale avant compression

export const useFileUploader = ({
  workorderId,
  uploadType,
  contactId,
  firstname,
  lastname,
  function: signFunction,
}: UseFileUploaderOptions) => {
  const media = useMedia({
    workorderId,
    initialMedia: [],
    endpoint: uploadType,
  });

  const compressImageIfNeeded = async (file: File): Promise<File> => {
    // Vérifier si c'est une image
    if (!file.type.startsWith("image/")) {
      return file;
    }

    // Vérifier la taille du fichier
    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      // Fichier trop gros, compression nécessaire
      try {
        toast.loading(t("Compression de l\"image en cours..."));
        const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);

        // Créer un nouveau fichier avec le nom original préservé
        const originalName = file.name;
        const finalFile = new File([compressedFile], originalName, {
          type: compressedFile.type,
          lastModified: Date.now()
        });

        const compressedSizeMB = finalFile.size / (1024 * 1024);

        toast.dismiss();
        toast.success(
          `Image compressée: ${fileSizeMB.toFixed(1)}MB → ${compressedSizeMB.toFixed(1)}MB`
        );

        return finalFile;
      } catch (error) {
        toast.dismiss();
        console.error("Erreur lors de la compression:", error);
        toast.error(t("workorder:files:errors:compressionFailed"));
        throw new Error(t("workorder:files:errors:compressionFailed"));
      }
    }

    if (fileSizeMB > 1) { // Compresser si > 1MB
      try {
        const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);

        // Créer un nouveau fichier avec le nom original préservé
        const originalName = file.name;
        const finalFile = new File([compressedFile], originalName, {
          type: compressedFile.type,
          lastModified: Date.now()
        });

        const compressedSizeMB = finalFile.size / (1024 * 1024);

        if (compressedSizeMB < fileSizeMB) {
          toast.success(
            `Image optimisée: ${fileSizeMB.toFixed(1)}MB → ${compressedSizeMB.toFixed(1)}MB`
          );
        }

        return finalFile;
      } catch (error) {
        console.warn("Erreur lors de la compression optionnelle:", error);
        return file; // Retourner le fichier original en cas d'erreur
      }
    }

    return file;
  };

  const handleUpload = async (file: File, type: "BEFORE" | "AFTER") => {
    if (uploadType === "signatures" && !contactId && !(firstname && lastname && signFunction)) {
      throw new Error("Contact or manual signatory info required for signature upload");
    }

    const processedFile = await compressImageIfNeeded(file);

    let extra: any = undefined;
    if (contactId) {
      extra = { contact: contactId };
    } else if (firstname && lastname && signFunction) {
      extra = { firstname, lastname, function: signFunction };
    }

    return media.uploadMedia(processedFile, type, extra);
  };

  return {
    loadingType: media.loadingType,
    handleUpload,
  };
};